import { useState, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, FolderKanban, Users, Bug, Clock, Search, X, LayoutGrid, List, Star, Archive } from 'lucide-react';
import { useProjects } from '../hooks/useProjects';
import { useToggleProjectFavorite } from '../hooks/useProjectFavorites';
import { useAuthStore } from '../store/authStore';
import Spinner from '../components/ui/Spinner';
import type { ProjectArchivedFilter } from '../services/projectService';

const TYPE_LABEL: Record<string, string> = {
  KANBAN: '칸반',
  SCRUM: '스크럼',
  CS: 'CS',
  TEAM: '팀 작업',
};

const TYPE_BADGE_COLORS: Record<string, string> = {
  SCRUM: 'bg-blue-100 text-blue-800',
  KANBAN: 'bg-green-100 text-green-800',
  CS: 'bg-purple-100 text-purple-800',
  TEAM: 'bg-orange-100 text-orange-800',
};

const VISIBILITY_LABEL: Record<string, string> = {
  PUBLIC: '공개',
  PRIVATE: '비공개',
  INTERNAL: '내부',
};

export default function ProjectListPage() {
  // CHG-132: 활성/보관 필터 토글 — 기본은 활성
  const [archivedFilter, setArchivedFilter] = useState<ProjectArchivedFilter>('active');
  const { data: projects, isLoading, error } = useProjects(archivedFilter);
  const user = useAuthStore((s) => s.user);
  const toggleFavorite = useToggleProjectFavorite();

  const canCreate = user?.role !== 'VIEWER';
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const isArchivedView = archivedFilter === 'archived';

  const handleSearchChange = useCallback((val: string) => {
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchQuery(val), 500);
  }, []);

  const handleSearchSubmit = useCallback(() => {
    clearTimeout(debounceRef.current);
    setSearchQuery(searchInput);
  }, [searchInput]);

  const filteredProjects = useMemo(() => {
    if (!projects) return [];
    let result = [...projects];

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(
        (p) => p.name.toLowerCase().includes(q) || p.projectKey.toLowerCase().includes(q),
      );
    }

    // 즐겨찾기 우선 정렬 [REQ-SB-016]
    result.sort((a, b) => {
      const aFav = (a as unknown as { isFavorite?: boolean }).isFavorite ? 1 : 0;
      const bFav = (b as unknown as { isFavorite?: boolean }).isFavorite ? 1 : 0;
      if (aFav !== bFav) return bFav - aFav;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });

    return result;
  }, [projects, searchQuery]);

  const handleToggleFavorite = (e: React.MouseEvent, projectId: string) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite.mutate(projectId);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="size-8" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-red-500">프로젝트 목록을 불러오지 못했습니다.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">프로젝트</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isArchivedView ? '보관된 프로젝트' : '내가 참여 중인 프로젝트'} {projects?.length ?? 0}개
            {searchQuery && ` · 검색 결과 ${filteredProjects.length}개`}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* CHG-132: 활성/보관 토글 */}
          <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setArchivedFilter('active')}
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                archivedFilter === 'active' ? 'bg-primary-50 text-primary-700' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              활성
            </button>
            <button
              onClick={() => setArchivedFilter('archived')}
              className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors ${
                archivedFilter === 'archived' ? 'bg-gray-100 text-gray-700' : 'text-gray-500 hover:text-gray-700'
              }`}
              title="보관함"
            >
              <Archive className="size-3.5" />
              보관함
            </button>
          </div>

          {/* 검색 */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') { handleSearchChange(''); e.currentTarget.blur(); }
                if (e.key === 'Enter') { e.preventDefault(); handleSearchSubmit(); }
              }}
              placeholder="프로젝트 검색..."
              className="pl-9 pr-8 py-2 w-56 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            {searchInput && (
              <button onClick={() => handleSearchChange('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="size-4" />
              </button>
            )}
          </div>

          {/* 뷰 전환 */}
          <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
            <button onClick={() => setViewMode('card')}
              className={`p-2 transition-colors ${viewMode === 'card' ? 'bg-primary-50 text-primary-600' : 'text-gray-400 hover:text-gray-600'}`}
              title="카드 뷰">
              <LayoutGrid className="size-4" />
            </button>
            <button onClick={() => setViewMode('list')}
              className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-primary-50 text-primary-600' : 'text-gray-400 hover:text-gray-600'}`}
              title="리스트 뷰">
              <List className="size-4" />
            </button>
          </div>

          {/* 새 프로젝트 */}
          {canCreate && (
            <Link to="/projects/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors shadow-sm">
              <Plus className="size-4" />
              새 프로젝트
            </Link>
          )}
        </div>
      </div>

      {/* Content */}
      {filteredProjects.length === 0 && searchQuery ? (
        <div className="flex flex-col items-center justify-center h-64 bg-white border border-gray-200 rounded-xl">
          <Search className="size-12 text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">검색 결과가 없습니다.</p>
          <button onClick={() => handleSearchChange('')}
            className="mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium">
            검색 초기화
          </button>
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 bg-white border border-gray-200 rounded-xl">
          {isArchivedView ? (
            <>
              <Archive className="size-12 text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">보관된 프로젝트가 없습니다.</p>
            </>
          ) : (
            <>
              <FolderKanban className="size-12 text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">참여 중인 프로젝트가 없습니다.</p>
              {canCreate && (
                <Link to="/projects/new" className="mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium">
                  첫 번째 프로젝트 만들기
                </Link>
              )}
            </>
          )}
        </div>
      ) : viewMode === 'list' ? (
        /* 리스트 뷰 */
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="grid grid-cols-[40px_1fr_80px_80px_60px_80px_100px] gap-4 px-4 py-3 bg-gray-50 border-b text-sm font-medium text-gray-500 uppercase">
            <span />
            <span>프로젝트</span>
            <span>유형</span>
            <span>공개 범위</span>
            <span>멤버</span>
            <span>작업</span>
            <span>업데이트</span>
          </div>

          {filteredProjects.map((project) => {
            const isFav = (project as unknown as { isFavorite?: boolean }).isFavorite === true;
            return (
              <Link key={project.id}
                to={`/projects/${project.projectKey}`}
                state={{ projectId: project.id }}
                className="grid grid-cols-[40px_1fr_80px_80px_60px_80px_100px] gap-4 px-4 py-3 items-center border-b last:border-b-0 hover:bg-gray-50 transition-colors text-sm">
                <button onClick={(e) => handleToggleFavorite(e, project.id)}
                  className="flex items-center justify-center">
                  <Star className={`size-4 ${isFav ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 hover:text-yellow-400'}`} />
                </button>
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="inline-flex items-center justify-center size-6 rounded bg-primary-100 text-primary-700 text-[11px] font-bold shrink-0">
                    {project.projectKey.slice(0, 2)}
                  </span>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">{project.name}</p>
                    <p className="text-sm text-gray-500">{project.projectKey}</p>
                  </div>
                </div>
                <span className="flex items-center gap-1">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-sm font-medium ${TYPE_BADGE_COLORS[project.projectType] || 'bg-gray-100 text-gray-800'}`}>
                    {TYPE_LABEL[project.projectType] ?? project.projectType}
                  </span>
                  {/* CHG-132: 보관 배지 */}
                  {project.archivedAt && (
                    <Archive className="size-3 text-gray-500" />
                  )}
                </span>
                <span className="text-gray-600 text-sm">{VISIBILITY_LABEL[project.visibility] ?? project.visibility}</span>
                <span className="text-gray-600 text-sm">{project.members.length}명</span>
                <span className="text-gray-600 text-sm">{project._count.issues}</span>
                <span className="text-gray-500 text-sm">{new Date(project.updatedAt).toLocaleDateString('ko-KR')}</span>
              </Link>
            );
          })}
        </div>
      ) : (
        /* 카드 뷰 */
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredProjects.map((project) => {
            const isFav = (project as unknown as { isFavorite?: boolean }).isFavorite === true;
            return (
              <Link key={project.id}
                to={`/projects/${project.projectKey}`}
                state={{ projectId: project.id }}
                className="block bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md hover:border-primary-200 transition-all group">
                <div className="flex items-start gap-3">
                  <div className="size-10 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0 group-hover:bg-primary-100 transition-colors">
                    <FolderKanban className="size-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 truncate group-hover:text-primary-700 transition-colors">
                      {project.name}
                    </h3>
                    <p className="text-sm text-gray-500 mt-0.5">{project.projectKey}</p>
                  </div>
                  <button onClick={(e) => handleToggleFavorite(e, project.id)}
                    className="p-1 rounded transition-colors shrink-0"
                    title={isFav ? '즐겨찾기 해제' : '즐겨찾기 추가'}>
                    <Star className={`size-4 ${isFav ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 hover:text-yellow-400'}`} />
                  </button>
                </div>

                {project.description && (
                  <p className="text-sm text-gray-600 mt-3 line-clamp-2">{project.description}</p>
                )}

                <div className="flex items-center gap-2 mt-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-sm font-medium ${TYPE_BADGE_COLORS[project.projectType] || 'bg-gray-100 text-gray-800'}`}>
                    {TYPE_LABEL[project.projectType] ?? project.projectType}
                  </span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-sm font-medium bg-gray-100 text-gray-600">
                    {VISIBILITY_LABEL[project.visibility] ?? project.visibility}
                  </span>
                  {/* CHG-132: 보관 배지 */}
                  {project.archivedAt && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-sm font-medium bg-gray-200 text-gray-700" title={`보관: ${new Date(project.archivedAt).toLocaleDateString('ko-KR')}`}>
                      <Archive className="size-3" />
                      보관됨
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-4 mt-4 pt-3 border-t border-gray-100 text-sm text-gray-500">
                  <span className="flex items-center gap-1"><Users className="size-3.5" />{project.members.length}명</span>
                  <span className="flex items-center gap-1"><Bug className="size-3.5" />작업 {project._count.issues}</span>
                  <span className="flex items-center gap-1 ml-auto"><Clock className="size-3.5" />{new Date(project.updatedAt).toLocaleDateString('ko-KR')}</span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
